import { useMemo } from "react";
import { Box, Stack, SystemProps, Text } from "@storyofams/react-ui";
import { ReactNode } from "hoist-non-react-statics/node_modules/@types/react";

import { findIndex } from "lodash";
import { useRouter } from "next/router";
import getSubTitle from "~lib/getSubTitle";
import { ProductByHandleQuery } from "~lib/shopify/sdk";
import { ImageSlider } from "../..";
import { Labels } from "../components";
import { ReviewsOverview } from "./Overview";

interface HeaderProps {
  product: ProductByHandleQuery["productByHandle"];
  children: ReactNode;
  isReviewsDisabled?: boolean;
  videoEmbed?: string;
  videoEmbedPreview?: string;
  embedPreview?: any;
}

export const HeaderContainer = ({
  product,
  children,
  isReviewsDisabled,
  videoEmbed,
  videoEmbedPreview,
  embedPreview,
  ...props
}: HeaderProps & SystemProps) => {
  const { query } = useRouter();
  const variant = useMemo(() => {
    if (query?.variantId) {
      const variant = product?.variants?.edges?.find(
        ({ node }) =>
          node.id === `gid://shopify/ProductVariant/${query?.variantId}`
      );

      return variant?.node;
    } else {
      const firstInStock = findIndex(product?.variants?.edges, function (item) {
        return item.node.availableForSale;
      });

      return product?.variants?.edges?.[firstInStock === -1 ? 0 : firstInStock]
        ?.node;
    }
  }, [product, query?.variantId]);
  const inStock = variant
    ? variant.availableForSale
    : product?.availableForSale;

  const getDiscount = (originalPrice, discountedPrice) =>
    Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  const discount = useMemo(() => {
    if (
      variant &&
      product.autoDiscount &&
      product?.autoDiscount?.value !== "0"
    ) {
      return String(product.autoDiscount.value);
    }
    return variant && variant?.compareAtPriceV2
      ? getDiscount(
          Number(variant?.compareAtPriceV2?.amount),
          Number(variant?.priceV2?.amount)
        )
      : null;
  }, [variant]);
  const date1 = new Date(product?.publishedAt);
  const date2 = new Date();
  const diffDays = Math.ceil(
    Math.abs(date2.getTime() - date1.getTime()) / (1000 * 3600 * 24)
  );
  const isNew = diffDays < 90;
  const { title, subTitle } = getSubTitle(product.title);
  return (
    <Box {...props} width="100%">
      <Stack
        border="1px solid #E2DFDF"
        borderRadius={"8px"}
        p={["15px", "30px"]}
        space={[0, 3, "40px"]}
        flexDirection={["column", "column", "row"]}
        justifyContent="center"
        width="100%"
        overflow={["hidden", "visible"]}
      >
        <Box
          flex={1}
          position="relative"
          width={["100%", "100%", "50%", "50%"]}
        >
          <Box mb="20px" display={["block", "none"]}>
            <Text
              fontFamily="DINPro"
              fontWeight={700}
              fontSize={"40px"}
              lineHeight={"48px"}
              mb={subTitle ? 1 : "10px"}
              data-id={"title"}
            >
              {title}
            </Text>
            {subTitle && (
              <Text mb={1} lineHeight={"22px"} fontWeight={500} color="#767473">
                {subTitle}
              </Text>
            )}
            {isReviewsDisabled && <ReviewsOverview id={product?.id} />}
          </Box>
          <Box position={"sticky"} top={150}>
            <ImageSlider
              product={product}
              key={product?.id}
              videoEmbed={videoEmbed}
              videoEmbedPreview={videoEmbedPreview}
              embedPreview={embedPreview}
              hideStockLabel
              variant={variant}
            />
            <Labels
              isPP={true}
              discount={discount as any}
              isNew={isNew}
              inStock={inStock}
              right={1}
              zIndex={3}
              top={1}
              position={"absolute"}
            />
          </Box>
        </Box>

        <Box
          flex={1}
          display={"flex"}
          flexDirection={"column"}
          width={["100%", "100%", "50%", "50%"]}
        >
          {children}
        </Box>
      </Stack>
    </Box>
  );
};
